import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Slider from '@mui/material/Slider';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import GetAppIcon from '@mui/icons-material/GetApp';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import SpeedIcon from '@mui/icons-material/Speed';
import TuneIcon from '@mui/icons-material/Tune';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UnifiedSettingsModal = ({
  open,
  onClose,
  conversationId,
  token,
  currentSettings,
  onSettingsUpdate
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Conversation Settings State
  const [responseDelays, setResponseDelays] = useState({
    min: currentSettings?.aiResponseDelaySettings?.minDelay || 2,
    max: currentSettings?.aiResponseDelaySettings?.maxDelay || 5
  });
  const [autoForwardDelay, setAutoForwardDelay] = useState(
    currentSettings?.autoForwardDelay || 3
  );
  const [notificationSettings, setNotificationSettings] = useState({
    soundEnabled: currentSettings?.notifications?.soundEnabled ?? true,
    desktopEnabled: currentSettings?.notifications?.desktopEnabled ?? false
  });

  // Voice Settings State
  const [voiceProvider, setVoiceProvider] = useState(
    currentSettings?.voiceSettings?.provider || 'elevenlabs'
  );
  const [selectedVoice, setSelectedVoice] = useState(
    currentSettings?.voiceSettings?.voiceId || ''
  );
  const [voiceSpeed, setVoiceSpeed] = useState(
    currentSettings?.voiceSettings?.speed || 1.0
  );
  const [voicePitch, setVoicePitch] = useState(
    currentSettings?.voiceSettings?.pitch || 1.0
  );

  // AI Configuration State
  const [suggestionFrequency, setSuggestionFrequency] = useState(
    currentSettings?.aiConfig?.suggestionFrequency || 'normal'
  );
  const [confidenceThreshold, setConfidenceThreshold] = useState(
    currentSettings?.aiConfig?.confidenceThreshold || 0.7
  );
  const [autoModeSwitch, setAutoModeSwitch] = useState(
    currentSettings?.aiConfig?.autoModeSwitch ?? true
  );

  // Export Settings State
  const [exportFormat, setExportFormat] = useState('pdf');
  const [includeAnnotations, setIncludeAnnotations] = useState(true);
  const [includeTimestamps, setIncludeTimestamps] = useState(true);
  const [includeSummary, setIncludeSummary] = useState(true);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setError('');
    setSaveSuccess(false);
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setError('');
    setSaveSuccess(false);

    try {
      const settingsData = {
        conversationId,
        aiResponseDelaySettings: {
          minDelay: responseDelays.min,
          maxDelay: responseDelays.max
        },
        autoForwardDelay,
        notifications: notificationSettings,
        voiceSettings: {
          provider: voiceProvider,
          voiceId: selectedVoice,
          speed: voiceSpeed,
          pitch: voicePitch
        },
        aiConfig: {
          suggestionFrequency,
          confidenceThreshold,
          autoModeSwitch
        }
      };

      // Call API to save settings
      // await api.updateConversationSettings(conversationId, settingsData, token);
      
      if (onSettingsUpdate) {
        onSettingsUpdate(settingsData);
      }
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      setError('Failed to save settings. Please try again.');
      console.error('Settings save error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTestVoice = () => {
    // Implement voice test functionality
    console.log('Testing voice with settings:', {
      provider: voiceProvider,
      voice: selectedVoice,
      speed: voiceSpeed,
      pitch: voicePitch
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          maxHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(0,0,0,0.08)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon />
          <Typography variant="h6">Conversation Settings</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="settings tabs">
            <Tab icon={<SettingsIcon />} label="General" />
            <Tab icon={<RecordVoiceOverIcon />} label="Voice" />
            <Tab icon={<SmartToyIcon />} label="AI Config" />
            <Tab icon={<GetAppIcon />} label="Export" />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* General Settings Tab */}
          <TabPanel value={activeTab} index={0}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Response Delays
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                AI Response Delay Range (seconds)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  type="number"
                  label="Min"
                  value={responseDelays.min}
                  onChange={(e) => setResponseDelays({ ...responseDelays, min: Number(e.target.value) })}
                  size="small"
                  sx={{ width: 100 }}
                  inputProps={{ min: 0, max: 10, step: 0.5 }}
                />
                <Typography>to</Typography>
                <TextField
                  type="number"
                  label="Max"
                  value={responseDelays.max}
                  onChange={(e) => setResponseDelays({ ...responseDelays, max: Number(e.target.value) })}
                  size="small"
                  sx={{ width: 100 }}
                  inputProps={{ min: 0, max: 10, step: 0.5 }}
                />
              </Box>
            </Box>

            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Auto-Forward Settings
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Auto-Forward Delay: {autoForwardDelay}s
              </Typography>
              <Slider
                value={autoForwardDelay}
                onChange={(e, value) => setAutoForwardDelay(value)}
                min={1}
                max={10}
                step={0.5}
                marks
                valueLabelDisplay="auto"
              />
            </Box>

            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Notifications
            </Typography>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.soundEnabled}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      soundEnabled: e.target.checked
                    })}
                  />
                }
                label="Sound Notifications"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={notificationSettings.desktopEnabled}
                    onChange={(e) => setNotificationSettings({
                      ...notificationSettings,
                      desktopEnabled: e.target.checked
                    })}
                  />
                }
                label="Desktop Notifications"
              />
            </Box>
          </TabPanel>

          {/* Voice Settings Tab */}
          <TabPanel value={activeTab} index={1}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Voice Provider
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Provider</InputLabel>
              <Select
                value={voiceProvider}
                onChange={(e) => setVoiceProvider(e.target.value)}
                label="Provider"
              >
                <MenuItem value="elevenlabs">ElevenLabs</MenuItem>
                <MenuItem value="google">Google Cloud TTS</MenuItem>
                <MenuItem value="azure">Azure Cognitive Services</MenuItem>
                <MenuItem value="browser">Browser TTS</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Voice Selection
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Voice</InputLabel>
              <Select
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
                label="Voice"
              >
                <MenuItem value="voice1">Professional Female</MenuItem>
                <MenuItem value="voice2">Warm Male</MenuItem>
                <MenuItem value="voice3">Neutral</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Voice Parameters
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Speed: {voiceSpeed}x
              </Typography>
              <Slider
                value={voiceSpeed}
                onChange={(e, value) => setVoiceSpeed(value)}
                min={0.5}
                max={2}
                step={0.1}
                marks
                valueLabelDisplay="auto"
              />
            </Box>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Pitch: {voicePitch}x
              </Typography>
              <Slider
                value={voicePitch}
                onChange={(e, value) => setVoicePitch(value)}
                min={0.5}
                max={2}
                step={0.1}
                marks
                valueLabelDisplay="auto"
              />
            </Box>

            <Button
              variant="outlined"
              startIcon={<VolumeUpIcon />}
              onClick={handleTestVoice}
              fullWidth
            >
              Test Voice Settings
            </Button>
          </TabPanel>

          {/* AI Configuration Tab */}
          <TabPanel value={activeTab} index={2}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Suggestion Settings
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Suggestion Frequency</InputLabel>
              <Select
                value={suggestionFrequency}
                onChange={(e) => setSuggestionFrequency(e.target.value)}
                label="Suggestion Frequency"
              >
                <MenuItem value="minimal">Minimal - Only critical moments</MenuItem>
                <MenuItem value="normal">Normal - Regular assistance</MenuItem>
                <MenuItem value="frequent">Frequent - Active guidance</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Confidence Settings
            </Typography>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Confidence Threshold: {(confidenceThreshold * 100).toFixed(0)}%
              </Typography>
              <Slider
                value={confidenceThreshold}
                onChange={(e, value) => setConfidenceThreshold(value)}
                min={0.5}
                max={1}
                step={0.05}
                marks={[
                  { value: 0.5, label: '50%' },
                  { value: 0.75, label: '75%' },
                  { value: 1, label: '100%' }
                ]}
                valueLabelDisplay="auto"
                valueLabelFormat={(value) => `${(value * 100).toFixed(0)}%`}
              />
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                AI suggestions below this threshold will require manual review
              </Typography>
            </Box>

            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Mode Switching
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={autoModeSwitch}
                  onChange={(e) => setAutoModeSwitch(e.target.checked)}
                />
              }
              label="Allow AI to suggest mode changes"
            />
          </TabPanel>

          {/* Export Settings Tab */}
          <TabPanel value={activeTab} index={3}>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Export Format
            </Typography>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Format</InputLabel>
              <Select
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value)}
                label="Format"
              >
                <MenuItem value="pdf">PDF Document</MenuItem>
                <MenuItem value="docx">Word Document</MenuItem>
                <MenuItem value="txt">Plain Text</MenuItem>
                <MenuItem value="json">JSON Data</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Include Options
            </Typography>
            <Box>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeAnnotations}
                    onChange={(e) => setIncludeAnnotations(e.target.checked)}
                  />
                }
                label="Include Annotations"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={includeTimestamps}
                    onChange={(e) => setIncludeTimestamps(e.target.checked)}
                  />
                }
                label="Include Timestamps"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={includeSummary}
                    onChange={(e) => setIncludeSummary(e.target.checked)}
                  />
                }
                label="Include AI Summary"
              />
            </Box>
          </TabPanel>

          {/* Status Messages */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {saveSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Settings saved successfully!
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSaveSettings}
          variant="contained"
          disabled={loading}
          endIcon={loading && <CircularProgress size={16} />}
          sx={{
            background: 'linear-gradient(135deg, #FF8C00 0%, #FFA500 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #FF7700 0%, #FF9500 100%)'
            }
          }}
        >
          Save Settings
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnifiedSettingsModal;